import type { AttachmentMediaType } from '../api.ts';

/**
 * The decisions behind preparing a screenshot for a support ticket, kept apart
 * from the canvas that carries them out so they can be tested in node.
 *
 * Two limits drive all of it. The server refuses an attachment over 700,000
 * bytes, and nginx in front of it refuses any request body over 1 MB. Base64
 * inflates by a third, so 700,000 bytes of image is about 933,000 characters on
 * the wire, which leaves room for a maximal 10,000-character message even if
 * every character is three bytes of UTF-8. A limit checked here rather than
 * discovered as a 413 is the difference between "make it smaller" and a reply
 * that silently never arrived.
 */

export const MAX_ATTACHMENT_BYTES = 700_000;

/**
 * The largest file we will try to decode at all. A phone photograph is a few
 * megabytes; something far larger is either not a screenshot or would stall the
 * tab while it is drawn, and is refused before it is read.
 */
export const MAX_SOURCE_BYTES = 25 * 1024 * 1024;

/** Longest side after scaling. Enough to read a screenshot of the console. */
export const MAX_DIMENSION = 1600;

/** What the file picker offers. Matches the server's list exactly. */
export const ACCEPTED_TYPES: readonly AttachmentMediaType[] = [
  'image/png',
  'image/jpeg',
  'image/webp',
];

export const ACCEPT_ATTRIBUTE = ACCEPTED_TYPES.join(',');

/** Qualities tried in order until the image fits. */
export const QUALITY_STEPS: readonly number[] = [0.85, 0.75, 0.65, 0.55, 0.45, 0.4];

export const isAcceptedType = (type: string): type is AttachmentMediaType =>
  (ACCEPTED_TYPES as readonly string[]).includes(type);

/** The quality to try after `current`, or null when there is nothing lower left. */
export const nextQuality = (current: number): number | null =>
  QUALITY_STEPS.find((step) => step < current) ?? null;

export const TOO_LARGE_AFTER_COMPRESSION =
  'That image is still larger than 700 KB after shrinking it. Crop it to the part that matters and try again.';

export const fitsLimit = (bytes: number): boolean => bytes <= MAX_ATTACHMENT_BYTES;

/**
 * Scale to fit inside a square of `max` on a side, never up. An image already
 * small enough keeps its size, because enlarging a screenshot only blurs it and
 * spends bytes on nothing.
 */
export const scaledSize = (
  width: number,
  height: number,
  max: number = MAX_DIMENSION,
): { width: number; height: number } => {
  const longest = Math.max(width, height);
  if (longest <= max) return { width, height };
  const ratio = max / longest;
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
};

/**
 * What an encoded blob actually is. `canvas.toBlob` falls back to PNG without
 * saying so when it cannot write the format it was asked for — Safari before 17
 * cannot write WebP — so the returned type is read rather than assumed. Anything
 * that is not WebP means "try JPEG", which every browser writes.
 */
export const encodedAs = (blobType: string): 'image/webp' | 'image/jpeg' | null =>
  blobType === 'image/webp' ? 'image/webp' : blobType === 'image/jpeg' ? 'image/jpeg' : null;

export type EncodeFormat = 'image/webp' | 'image/jpeg';

export interface Attempt {
  readonly format: EncodeFormat;
  readonly quality: number;
}

export const FIRST_ATTEMPT: Attempt = { format: 'image/webp', quality: QUALITY_STEPS[0] ?? 0.85 };

/**
 * What to do after one encoding attempt. `blob` is what `canvas.toBlob` gave
 * back, or null when it gave nothing.
 *
 * - The wrong format back means the browser has no encoder for WebP: start
 *   again in JPEG from the top quality, and redraw on an opaque ground.
 * - The wrong format back from a JPEG attempt, or nothing at all, cannot be
 *   fixed by trying again.
 * - Too big steps the quality down, until there is no step left.
 */
export type Decision<B> =
  | { readonly kind: 'done'; readonly mediaType: EncodeFormat; readonly blob: B }
  | { readonly kind: 'retry'; readonly attempt: Attempt; readonly redraw: boolean }
  | { readonly kind: 'fail'; readonly reason: string };

export const CANNOT_ENCODE = 'This browser could not encode the image.';

export const decide = <B extends { type: string; size: number }>(
  attempt: Attempt,
  blob: B | null,
): Decision<B> => {
  const actual = blob === null ? null : encodedAs(blob.type);
  if (blob === null || actual !== attempt.format) {
    return attempt.format === 'image/webp'
      ? { kind: 'retry', attempt: { format: 'image/jpeg', quality: FIRST_ATTEMPT.quality }, redraw: true }
      : { kind: 'fail', reason: CANNOT_ENCODE };
  }
  if (fitsLimit(blob.size)) return { kind: 'done', mediaType: attempt.format, blob };
  const quality = nextQuality(attempt.quality);
  return quality === null
    ? { kind: 'fail', reason: TOO_LARGE_AFTER_COMPRESSION }
    : { kind: 'retry', attempt: { format: attempt.format, quality }, redraw: false };
};

/** The API wants bare base64, not a data URL. */
export const stripDataUrl = (dataUrl: string): string => {
  const comma = dataUrl.indexOf(',');
  return dataUrl.startsWith('data:') && comma !== -1 ? dataUrl.slice(comma + 1) : dataUrl;
};

/**
 * The preview's address. A data URL rather than an object URL: the console's
 * Content-Security-Policy is set in nginx, and `data:` images are already known
 * to render under it (the branding logo preview uses them) where `blob:` is not.
 */
export const toDataUrl = (attachment: { mediaType: string; data: string }): string =>
  `data:${attachment.mediaType};base64,${attachment.data}`;

/** Why a chosen file cannot be used, before any work is done on it. */
export const sourceProblem = (file: { type: string; size: number }): string | null => {
  if (!isAcceptedType(file.type)) return 'Choose a PNG, JPEG or WebP image.';
  if (file.size > MAX_SOURCE_BYTES) {
    return 'That image is too large to prepare in the browser. Take a smaller screenshot or crop it first.';
  }
  return null;
};

/** A byte count for a person: "412 KB". */
export const kilobytes = (bytes: number): string => `${Math.max(1, Math.round(bytes / 1000))} KB`;
